import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { take, switchMap, tap, of } from 'rxjs';
import { IPeerGroupCompetitors } from '../../../../../../core/models/results.model';
import { changeActiveSideTab, loadSidenavData, setUniverseSelection, submitSearch } from '../../../../../../state/action';
import { AppState, IUniverseSelection } from '../../../../../../state/app.state';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { RouterLink, RouterOutlet } from '@angular/router';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { AddToWatchlistPopupComponent } from 'src/app/shared/components/add-to-watchlist-popup/add-to-watchlist-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { UniverseSelectorComponent } from 'src/app/shared/components/universe-selector/universe-selector.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { REGION_CODES } from 'public/country-region-codes';
import * as XLSX from 'xlsx';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';

// Extended interface for public competitors with monitoring fields
interface IPublicCompetitor extends IPeerGroupCompetitors {
  innovation_score_previous?: number;
  innovation_score_change?: number;
  rank_previous?: number;
  rank_climbed?: number;
  pct_rank_climbed?: number;
  score_bracket?: number;
  rank_bracket?: number;
  peer_group_size?: number;
  date_eom?: string;
}

@Component({
  selector: 'app-public-competitors',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSlideToggleModule,
    MainNavbarComponent,
    MatTooltipModule,
    MatOptionModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatInputModule,
    MatIconModule,
    NullableNumberPipe
  ],
  templateUrl: './public-competitors.component.html',
  styleUrl: './public-competitors.component.css'
})
export class PublicCompetitorsComponent implements OnInit, OnChanges {
  @Input() tabId!: string;
  @Input() query: string | null = null;
  @Input() gsubind: string[] | null = null;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() side_nav_name: string | null = null;
  entityid: string | undefined;
  showDataMissing: boolean = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog
  ) { }

  showResults: boolean = false;
  competitors_list_loading_failed: boolean = false;
  competitors_list: IPublicCompetitor[] = [];
  showQueryBox: boolean = false;
  company_data: IPublicCompetitor | undefined = undefined;
  filteredCompanyList: IPublicCompetitor[] = [];
  paginator_1_page: number = 1;
  showOnlyFavorites: boolean = false;
  searchQuery: string = '';
  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();

  countryFilterOptions = REGION_CODES;

  // Sorting state
  sortColumn: string = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Selection state
  selectedCompanies: Set<string> = new Set();

  // Category system
  selectedCategory: string = 'all_competitors';
  categoryTabs = [
    {
      key: 'all_competitors',
      label: 'All Competitors',
      tooltip: 'All companies in GICS, no filter, innovation score ranking.',
      description: 'All companies in the selected peer group, ranked by innovation score.'
    },
    {
      key: 'innovation_leaders',
      label: 'Innovation Leaders',
      tooltip: 'Innovation Leaders are companies that rank highest among competitors based on their innovation score, a composite score reflecting size, growth and quality, relative to the peer group.',
      description: 'Innovation Leaders are the 50 companies that stand out with a strong innovation activity balanced across portfolio size, recent growth and innovation quality.'
    },
    {
      key: 'growth_leaders',
      label: 'Growth Leaders',
      tooltip: 'Growth Leaders are companies with the highest relative growth, among companies with at least 20 inventions published in the last 12 months.',
      description: 'Growth Leaders are the 50 companies which show the most significant recent acceleration in their innovation activity.'
    },
    {
      key: 'size_leaders',
      label: 'Size Leaders',
      tooltip: 'Size Leaders are companies with the highest number of inventions published in the last 10 years.',
      description: 'Size Leaders are the 50 companies with the largest innovation portfolios among competitors, responsible for the largest share of the innovation in their space.'
    },
    {
      key: 'quality_leaders',
      label: 'Quality Leaders',
      tooltip: 'Top Inventors are companies with the highest innovation quality, among companies with at least 10 inventions in the last 12m.',
      description: 'Quality Leaders are the 50 companies that prioritize quality over quantity, likely to produce the largest individual breakthroughs.'
    },
    {
      key: 'upcoming',
      label: 'Up and Coming',
      tooltip: 'Up and Coming are companies with the highest innovation score increase, among companies with non-negative innovation score change.',
      description: 'Up and Coming are the 50 companies with the highest innovation score increase, showing the strongest recent improvement in innovation activity.'
    },
  ];

  ngOnInit(): void { }

  toggleQueryBox(): void {
    this.showQueryBox = !this.showQueryBox;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.resetState();
      if (this.gsubind && this.gsubind[0]) {
        this.getCompetitors();
      }
    }
  }

  resetState(): void {
    this.showResults = false;
    this.showDataMissing = false;
    this.competitors_list = [];
    this.competitors_list_loading_failed = false;
    this.selectedCategory = 'all_competitors';
  }

  getCompetitors(): void {
    this.entityid = this.entityids![0];
    this.showOnlyFavorites = false;
    this.showResults = true;
    this.store
      .select(selectSidenavDataByTabIdAndKey(this.tabId, 'company_public_competitors'))
      .pipe(
        take(1),
        switchMap((data) => {
          if (!data) {
            return this.apiService.getCompetitiveBenchmarkingPeerGroup(
              { universeSelection: { include_public: true, include_private: false } },
              this.tabId,
              this.entityids![0],
              this.gsubind![0]
            ).pipe(
              tap((res) => {
                if (res.status === 204 || !res.body?.payload?.results?.length) {
                  this.showDataMissing = true;
                  return;
                }
                const payload = res.body!.payload;

                if (payload.tabId !== this.tabId) {
                  this.store.dispatch(loadSidenavData({
                    tabId: payload.tabId,
                    sidenavKey: 'company_public_competitors',
                    data: payload.results
                  }));
                  return;
                }

                if (payload.results != null) {
                  this.store.dispatch(loadSidenavData({
                    tabId: this.tabId,
                    sidenavKey: 'company_public_competitors',
                    data: payload.results
                  }));
                  this.processCompetitorData(payload.results as IPublicCompetitor[]);
                } else {
                  this.competitors_list_loading_failed = true;
                }
              })
            );
          } else {
            this.competitors_list_loading_failed = false;
            this.processCompetitorData(data as IPublicCompetitor[]);
            return of();
          }
        })
      )
      .subscribe({
        error: () => (this.competitors_list_loading_failed = true),
      });
  }

  processCompetitorData(data: IPublicCompetitor[]): void {
    this.competitors_list = data.map((company) => ({
      ...company,
      favorite: company.favorite ?? false,
    }));

    this.filteredCompanyList = [...this.competitors_list];
    this.applyFilters();
  }

  toggleFavorite(company: IPublicCompetitor): void {
    const updatedCompany = { ...company, favorite: !company.favorite };
    const index = this.competitors_list.indexOf(company);
    if (index !== -1) {
      this.competitors_list[index] = updatedCompany;
    }
    this.applyFilters();
  }

  toggleExpand(region: string): void {
    if (this.expandedRegions.has(region)) {
      this.expandedRegions.delete(region);
    } else {
      this.expandedRegions.add(region);
    }
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const allSelected = this.isRegionFullySelected(region);
    const regionCountryCodes = region.countries.map((c: any) => c.code);

    if (allSelected) {
      const updatedSelection = (this.countriesControl.value ?? []).filter(
        (value: string) => !regionCountryCodes.includes(value)
      );
      this.countriesControl.setValue(updatedSelection);
    } else {
      const updatedSelection = Array.from(
        new Set([...(this.countriesControl.value ?? []), ...regionCountryCodes])
      );
      this.countriesControl.setValue(updatedSelection);
    }
    this.applyFilters();
  }

  toggleCountrySelection(countryCode: string, region: any): void {
    const currentSelection = this.countriesControl.value ?? [];
    const isSelected = currentSelection.includes(countryCode);

    if (isSelected) {
      this.countriesControl.setValue(
        currentSelection.filter((value: string) => value !== countryCode)
      );
    } else {
      this.countriesControl.setValue([...currentSelection, countryCode]);
    }
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const selectedCodes = this.countriesControl.value ?? [];
    return regionCountryCodes.every((code: string) => selectedCodes.includes(code));
  }

  filterFavorites(): void {
    this.showOnlyFavorites = !this.showOnlyFavorites;
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredCompanyList = this.competitors_list;

    // Apply category filter first
    this.filteredCompanyList = this.applyCategoryFilter(this.filteredCompanyList);

    // Apply category ranking (re-sort if needed, then assign ranks) BEFORE filtering by selection
    // This ensures ranks are set and won't be recalculated when showing selection only
    this.applyCategoryRanking(this.filteredCompanyList);

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.countriesControl.value!.includes(company.country)
      );
    }

    if (this.showOnlyFavorites) {
      // Show selected companies (starred/selected via star icon)
      // Ranks are already set above, so they won't be recalculated
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.selectedCompanies.has(company.entityid) || company.favorite
      );
      this.paginator_1_page = 1;
      // Skip sorting when showing selection only to preserve ranks
      return;
    }

    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        company.entity_name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
  }

  sort(column: string): void {
    // Only allow sorting when category is 'all_competitors'
    if (this.selectedCategory !== 'all_competitors') {
      return;
    }
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySorting();
  }

  applySorting(): void {
    if (!this.sortColumn) return;
    this.paginator_1_page = 1;
    this.filteredCompanyList.sort((a, b) => {
      let aVal: any = 0, bVal: any = 0;

      switch (this.sortColumn) {
        case 'rank':
          aVal = a.rank || 0;
          bVal = b.rank || 0;
          break;
        case 'size':
          aVal = a.invention_count_120m || 0;
          bVal = b.invention_count_120m || 0;
          break;
        case 'growth':
          aVal = a.relative_growth_12m || 0;
          bVal = b.relative_growth_12m || 0;
          break;
        case 'quality':
          aVal = a.quality_average_12m || 0;
          bVal = b.quality_average_12m || 0;
          break;
        case 'innovation_score':
          aVal = a.innovation_score || 0;
          bVal = b.innovation_score || 0;
          break;
        case 'score_change':
          aVal = a.innovation_score_change || 0;
          bVal = b.innovation_score_change || 0;
          break;
        case 'rank_change':
          aVal = a.pct_rank_climbed || 0;
          bVal = b.pct_rank_climbed || 0;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }


  getSortIcon(column: string): string {
    if (this.selectedCategory !== 'all_competitors') return 'bi-arrow-down-up';
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }
  newSearch(company: IPublicCompetitor) {
    if (!company.gsubind_extended) {
      company.gsubind_extended = Array.isArray(company.gsubind) ? company.gsubind[0] : company.gsubind;
    }
    const searchParams = { 'companies': [company] };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'company' }));
  }

  getBarColor(score: number | undefined): string {
    if (!score) return 'red';
    if (score >= 90) return 'green';
    if (score >= 50) return 'orange';
    return 'red';
  }

  getOrdinalSuffix(rank: number | undefined): string {
    if (!rank) return '';
    if (rank % 10 === 1 && rank % 100 !== 11) return 'st';
    if (rank % 10 === 2 && rank % 100 !== 12) return 'nd';
    if (rank % 10 === 3 && rank % 100 !== 13) return 'rd';
    return 'th';
  }

  getIndustryLevel(level: number | string | undefined) {
    switch (level) {
      case '1':
      case 1:
        return 'Sector';
      case '2':
      case 2:
        return 'Sub-Sector';
      case '3':
      case 3:
        return 'Industry';
      case '4':
      case 4:
        return 'Sub-Industry';
      default:
        return 'Unknown';
    }
  }

  getBracketColor(bracket: number | undefined): string {
    if (!bracket) return 'gray';
    const colors = ['dark-red', 'dark-red', 'gray', 'dark-green', 'dark-green'];
    return colors[bracket - 1] || 'gray';
  }

  getBracketArrow(bracket: number | undefined): string {
    if (!bracket) return '';
    if (bracket === 1) return 'bi-chevron-double-down';
    if (bracket === 2) return 'bi-chevron-down';
    if (bracket === 4) return 'bi-chevron-up';
    if (bracket === 5) return 'bi-chevron-double-up';
    return '';
  }

  getBracketTooltip(bracket: number | undefined) {
    if (!bracket) return '';
    if (bracket === 1) return 'Among the bottom 5% globally.';
    if (bracket === 2) return 'Among the bottom 10% globally.';
    if (bracket === 3) return null;
    if (bracket === 4) return 'Among the top 10% globally.';
    if (bracket === 5) return 'Among the top 5% globally.';
    return null;
  }

  downloadExcel(): void {
    const filteredList = this.competitors_list.map(item => ({
      Company: item.entity_name,
      ISIN: item.isin,
      Country: item.country,
      Innovation_Score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
      Score_Change_3_Months: item.innovation_score_change ? Math.round(item.innovation_score_change * 10) / 10 : item.innovation_score_change,
      Rank_in_Peer_Group: item.rank ? `${item.rank}${this.getOrdinalSuffix(item.rank)} (out of ${item.peer_group_size || 'N/A'})` : '',
      Rank_Climb_3_Months: item.rank_climbed,
      Percentile_Rank_Climb_3_Months: item.pct_rank_climbed ? Math.round(item.pct_rank_climbed * 10) / 10 : item.pct_rank_climbed,
      Score_Change_Warning: this.getBracketText(item.score_bracket),
      Rank_Change_Warning: this.getBracketText(item.rank_bracket)
    }));

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredList);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 25 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 25 }, { wch: 25 }
    ];
    const sheetName = "public_competitors";
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const tabName = this.tab_name ? this.tab_name.substring(0, 30) : 'public_competitors';
    const fileName = this.tab_name ? `${tabName}_public_competitors.xlsx` : 'public_competitors.xlsx';
    XLSX.writeFile(wb, fileName);
  }

  getBracketText(bracket: number | undefined): string {
    if (bracket === 1) return 'Severe Red Flag (Bottom 5%)';
    if (bracket === 2) return 'Red Flag (Bottom 10%)';
    if (bracket === 3) return 'None';
    if (bracket === 4) return 'Green Flag (Top 10%)';
    if (bracket === 5) return 'Strong Green Flag (Top 5%)';
    return '';
  }



  // Check if company is eligible for watchlist (public + GICS)
  isEligibleForWatchlist(company: IPublicCompetitor): boolean {
    return ((company.companytype === 'Public Company' || company.companytype === 'Public Investment Firm') && !!(company.gsubind || company.gsubind_extended || (company as any).entity_gics));
  }

  // Toggle company selection - allow selecting any company, but show warning when adding to watchlist
  toggleCompanySelection(company: IPublicCompetitor): void {
    if (this.selectedCompanies.has(company.entityid)) {
      this.selectedCompanies.delete(company.entityid);
    } else {
      this.selectedCompanies.add(company.entityid);
    }
  }

  // Check if company is selected
  isCompanySelected(entityid: string): boolean {
    return this.selectedCompanies.has(entityid);
  }

  // Get selected eligible companies
  getSelectedEligibleCompanies(): IPublicCompetitor[] {
    return this.filteredCompanyList.filter(c =>
      this.selectedCompanies.has(c.entityid) && this.isEligibleForWatchlist(c)
    );
  }

  // Toggle select all eligible companies
  toggleSelectAll(): void {
    const eligibleCompanies = this.filteredCompanyList.filter(c => this.isEligibleForWatchlist(c));
    const allSelected = eligibleCompanies.every(c => this.selectedCompanies.has(c.entityid));

    if (allSelected) {
      // Deselect all
      eligibleCompanies.forEach(c => this.selectedCompanies.delete(c.entityid));
    } else {
      // Select all
      eligibleCompanies.forEach(c => this.selectedCompanies.add(c.entityid));
    }
  }




  selectCategory(categoryKey: string): void {
    this.selectedCategory = categoryKey;
    this.paginator_1_page = 1;
    if (categoryKey !== 'all_competitors') {
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
    }
    this.applyFilters();
  }
  // Get current category label
  getCurrentCategoryLabel(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.label : 'All Competitors';
  }

  // Get current category description
  getCurrentCategoryDescription(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.description : '';
  }

  // Get current category tooltip (info text)
  getCurrentCategoryTooltip(): string {
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    return category ? category.tooltip : '';
  }

  // Check if current category has results
  hasCategoryResults(): boolean {
    return this.filteredCompanyList.length > 0;
  }

  // Apply category filter based on selected category
  applyCategoryFilter(companies: IPublicCompetitor[]): IPublicCompetitor[] {
    if (!companies || companies.length === 0) return [];

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        // Top 50 with innovation score > 0
        return companies
          .filter(company => (company.innovation_score ?? 0) > 0)
          .sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0))
          .slice(0, 50);

      case 'growth_leaders':
        // Top 50 by growth with minimum 10 inventions in last year and relative growth > 0
        return companies
          .filter(company => ((company.invention_count_12m ?? 0) >= 10) && (company.relative_growth_12m ?? 0) > 0)
          .sort((a, b) => (b.relative_growth_12m ?? 0) - (a.relative_growth_12m ?? 0))
          .slice(0, 50);

      case 'size_leaders':
        // Top 50 by size (10y invention count)
        return companies
          .filter(company => (company.invention_count_120m ?? 0) > 0)
          .sort((a, b) => (b.invention_count_120m ?? 0) - (a.invention_count_120m ?? 0))
          .slice(0, 50);

      case 'quality_leaders':
        // Top 50 by quality with minimum 10 inventions in last year and quality average > 69
        return companies
          .filter(company => ((company.invention_count_12m ?? 0) >= 10) && (company.quality_average_12m ?? 0) > 69)
          .sort((a, b) => (b.quality_average_12m ?? 0) - (a.quality_average_12m ?? 0))
          .slice(0, 50);

      case 'upcoming':
        // Top 50 by innovation score increase (non-negative)
        return companies
          .filter(company => (company.innovation_score_change ?? 0) >= 0)
          .sort((a, b) => (b.innovation_score_change ?? 0) - (a.innovation_score_change ?? 0))
          .slice(0, 50);

      case 'all_competitors':
      default:
        return companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
    }
  }

  // Apply category-specific ranking
  private applyCategoryRanking(companies: IPublicCompetitor[]): void {
    switch (this.selectedCategory) {
      case 'innovation_leaders':
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'growth_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'size_leaders':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'quality_leaders':
        // Already sorted by quality in applyCategoryFilter, just assign ranks
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'upcoming':
        companies.forEach((c, i) => c.rank = i + 1);
        break;

      case 'all_competitors':
      default:
        companies.sort((a, b) => (b.innovation_score ?? 0) - (a.innovation_score ?? 0));
        companies.forEach((c, i) => c.rank = i + 1);
        break;
    }
  }

  // Open Add to Watchlist popup for selected companies
  openAddToWatchlist(): void {
    const allSelected = this.filteredCompanyList.filter(c => this.selectedCompanies.has(c.entityid));
    const eligibleSelected = this.getSelectedEligibleCompanies();
    const privateSelected = allSelected.filter(c => !this.isEligibleForWatchlist(c));

    // Show warning if private companies are selected
    if (privateSelected.length > 0) {
      const message = `Out of ${allSelected.length} selected, only ${eligibleSelected.length} will be added. ${privateSelected.length} private company/companies will not be added. Only public companies with a valid GICS will be added.`;
      if (!confirm(message + '\n\nDo you want to continue?')) {
        return;
      }
    }

    if (eligibleSelected.length === 0) {
      alert('Only public companies with a valid GICS will be added to watchlist.');
      return;
    }

    const dialogRef = this.dialog.open(AddToWatchlistPopupComponent, {
      width: '550px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      disableClose: true,
      data: {
        companies: eligibleSelected.map(c => ({
          entityid: c.entityid,
          entity_name: c.entity_name,
          entity_gics: c.gsubind_extended || (Array.isArray(c.gsubind) ? c.gsubind[0] : c.gsubind),
          isin: c.isin,
          companytype: c.companytype,
          gsubind: c.gsubind,
          gsubind_extended: c.gsubind_extended
        }))
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Clear selection after adding
      // this.selectedCompanies.clear();
    });
  }

  getCategoryDescription(company: IPeerGroupCompetitors): string {
    const companyName = company.entity_name || 'Unknown Company';
    const category = this.categoryTabs.find(cat => cat.key === this.selectedCategory);
    const subindustryName = company.gics_custom_name || company.gsubind_extended_name || company.subindustryname;
    const industryLevel = this.getIndustryLevel(company.gics_custom_level);
    if (!category) return '';

    const isCompanyInCategory = this.filteredCompanyList.some(c => c.entityid === company.entityid);
    const hasCountryFilter = this.countriesControl.value && this.countriesControl.value.length > 0;
    const displayCount = hasCountryFilter ? this.filteredCompanyList.length : this.getBaseCategoryCount();
    const countryFilterText = hasCountryFilter ? ' and selected countries' : '';

    switch (this.selectedCategory) {
      case 'innovation_leaders':
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> among the <b>${displayCount}</b> <b>Innovation Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName} (GICS Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Innovation Leaders</b> in the selected peer group andthe GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'growth_leaders':
        if (isCompanyInCategory) {
          const growthRank = this.getGrowthRank(company);
          return `<b>${companyName}</b> ranks <b>${growthRank}${this.getOrdinalSuffix(growthRank)}</b> among the <b>${displayCount}</b> <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Growth Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'size_leaders':
        if (isCompanyInCategory) {
          const sizeRank = this.getSizeRank(company);
          return `<b>${companyName}</b> ranks <b>${sizeRank}${this.getOrdinalSuffix(sizeRank)}</b> among the <b>${displayCount}</b> <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Size Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}`;
        }

      case 'upcoming':
        if (isCompanyInCategory) {
          const upcomingrank = this.getUpcomingRank(company);
          return `<b>${companyName}</b> ranks <b>${upcomingrank}${this.getOrdinalSuffix(upcomingrank)}</b> among the <b>${displayCount}</b> <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Up and Coming</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'quality_leaders':
        if (isCompanyInCategory) {
          const qualityRank = this.getQualityRank(company);
          return `<b>${companyName}</b> ranks <b>${qualityRank}${this.getOrdinalSuffix(qualityRank)}</b> among the <b>${displayCount}</b> <b>Quality Leaders</b>  competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not one of the <b>Quality Leaders</b> in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }

      case 'all_competitors':
      default:
        if (isCompanyInCategory) {
          return `<b>${companyName}</b> ranks <b>${company.rank}${this.getOrdinalSuffix(company.rank)}</b> in innovation strength among <b>${displayCount}</b> competitors in the selected peer group and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        } else {
          return `<b>${companyName}</b> is not present in the current peer group selection and the GICS ${industryLevel} <b>${subindustryName}</b> (Level ${company.gics_custom_level} out of 4) ${countryFilterText}.`;
        }
    }
  }
  private getBaseCategoryCount(): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    return baseFiltered.length;
  }
  private getGrowthRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  private getSizeRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }


  private getQualityRank(company: IPeerGroupCompetitors): number {
    const index = this.filteredCompanyList.findIndex(c => c.entityid === company.entityid);
    return index >= 0 ? index + 1 : 0;
  }

  getUpcomingRank(company: IPeerGroupCompetitors): number {
    const baseFiltered = this.applyCategoryFilter([...this.competitors_list]);
    const upcomingCompanies = baseFiltered.filter(c => {
      const inv1y = c.invention_count_12m;
      const inv10y = c.invention_count_120m;
      const growth = c.relative_growth_12m;
      return inv1y >= 10 && growth > 0 && (inv10y > 0 ? (inv1y / inv10y) >= 0.33 : false);
    });

    const sortedCompanies = upcomingCompanies.sort((a, b) => (b.innovation_score) - (a.innovation_score));
    const companyIndex = sortedCompanies.findIndex(c => c.entityid === company.entityid);
    return companyIndex >= 0 ? companyIndex + 1 : 0;
  }


}
